import { ENEMY_PROFILES } from "../content/game-content";
import { CENTER, direction, length, shortestAngle } from "../engine/math";
import {
  getEstimatedImpactTime,
  getProjectileForecast,
  getTowerLaunchAngle,
  getTowerPosition
} from "../engine/simulation";
import type { Enemy, GameEvent, GameState, Projectile, Tower, Vec2 } from "../engine/types";
import { calculateCamera, type CameraLayout } from "./camera";

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
};

export class GameRenderer {
  private readonly context: CanvasRenderingContext2D;
  private width = 1;
  private height = 1;
  private scale = 1;
  private origin = { x: 0, y: 0 };
  private layout: CameraLayout = calculateCamera(1, 1);
  private particles: Particle[] = [];
  private time = 0;
  private reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  private readonly battlefield = new Image();
  private readonly hearthstead = new Image();
  private readonly kiteBallista = new Image();
  private readonly raiderAtlas = new Image();
  private readonly redTempest = new Image();

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) {
      throw new Error("Canvas 2D is required to play Kiteward.");
    }
    this.context = context;
    this.battlefield.src = "/art/brass-orchard-arena-v1.webp";
    this.hearthstead.src = "/art/hearthstead-v2.webp";
    this.kiteBallista.src = "/art/kite-ballista-v2.webp";
    this.raiderAtlas.src = "/art/raider-atlas-v2.webp";
    this.redTempest.src = "/art/red-tempest-v2.webp";
    this.resize();
    window.addEventListener("resize", this.resize);
    window.visualViewport?.addEventListener("resize", this.resize);
    new ResizeObserver(this.resize).observe(this.canvas);
  }

  render(state: GameState, dt: number): void {
    this.time += dt;
    this.updateParticles(dt, state);
    this.drawSky(state);
    this.beginWorld();
    this.drawStreamlines(state);
    this.drawArena();
    this.drawForecast(state);
    this.drawTowers(state);
    this.drawHearth(state);
    this.drawProjectiles(state.projectiles);
    this.drawEnemies(state);
    this.drawParticles();
    this.endWorld();
    this.drawOffscreenThreats(state);
    this.drawWindInstrument(state);
    this.drawGuidance(state);
  }

  receive(events: GameEvent[], state: GameState): void {
    for (const event of events) {
      if (event.kind === "launch" && event.x !== undefined && event.y !== undefined) {
        this.burst(event.x, event.y, "#f7d77b", 4, 34);
      } else if (event.kind === "hit" && event.x !== undefined && event.y !== undefined) {
        this.burst(event.x, event.y, "#f7efd1", 8, 74);
      } else if (event.kind === "curved-hit" && event.x !== undefined && event.y !== undefined) {
        this.burst(event.x, event.y, "#8fe8ed", 12, 88);
      } else if (event.kind === "projectile-lost" && event.x !== undefined && event.y !== undefined) {
        this.burst(event.x, event.y, "#d8b971", 6, 38);
      } else if (event.kind === "defeat-enemy" && event.x !== undefined && event.y !== undefined) {
        this.burst(event.x, event.y, event.enemyKind ? ENEMY_PROFILES[event.enemyKind].color : "#ef8354", 20, 110);
      } else if (event.kind === "gust") {
        const wind = direction(state.wind.angle);
        for (let index = 0; index < 38; index += 1) {
          const offset = (index / 37 - 0.5) * Math.PI * 0.8;
          const angle = state.wind.angle + offset;
          const speed = 150 + Math.abs(index - 18) * 2;
          this.particles.push({
            x: CENTER.x + Math.cos(angle) * 82,
            y: CENTER.y + Math.sin(angle) * 82,
            vx: wind.x * speed + Math.cos(angle) * 90,
            vy: wind.y * speed + Math.sin(angle) * 90,
            life: 0.62,
            maxLife: 0.62,
            color: "#a5f6ed",
            size: 5
          });
        }
      } else if (event.kind === "hearth-hit") {
        this.burst(CENTER.x, CENTER.y, "#ff704f", 28, 130);
      }
    }
  }

  screenToWorld(clientX: number, clientY: number): Vec2 {
    const bounds = this.canvas.getBoundingClientRect();
    const x = clientX - bounds.left;
    const y = clientY - bounds.top;
    return {
      x: (x - this.origin.x) / this.scale,
      y: (y - this.origin.y) / this.scale
    };
  }

  private readonly resize = (): void => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const bounds = this.canvas.getBoundingClientRect();
    this.width = Math.max(1, bounds.width);
    this.height = Math.max(1, bounds.height);
    this.canvas.width = Math.round(this.width * dpr);
    this.canvas.height = Math.round(this.height * dpr);
    this.context.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.layout = calculateCamera(this.width, this.height);
    this.scale = this.layout.scale;
    this.origin = this.layout.origin;
  };

  private drawSky(state: GameState): void {
    const context = this.context;
    context.setTransform(context.getTransform().a, 0, 0, context.getTransform().d, 0, 0);
    context.fillStyle = "#06131d";
    context.fillRect(0, 0, this.width, this.height);
    if (this.battlefield.complete && this.battlefield.naturalWidth > 0) {
      const imageScale = Math.max(
        this.width / this.battlefield.naturalWidth,
        this.height / this.battlefield.naturalHeight
      );
      const imageWidth = this.battlefield.naturalWidth * imageScale;
      const imageHeight = this.battlefield.naturalHeight * imageScale;
      context.drawImage(
        this.battlefield,
        (this.width - imageWidth) / 2,
        (this.height - imageHeight) / 2,
        imageWidth,
        imageHeight
      );
    }
    const gradient = context.createRadialGradient(
      this.width * 0.5,
      this.height * 0.48,
      0,
      this.width * 0.5,
      this.height * 0.48,
      Math.max(this.width, this.height) * 0.78
    );
    gradient.addColorStop(0, state.hearth.flash > 0 ? "rgba(77, 35, 44, 0.52)" : "rgba(11, 53, 62, 0.14)");
    gradient.addColorStop(0.42, "rgba(4, 29, 40, 0.2)");
    gradient.addColorStop(1, "rgba(1, 7, 13, 0.62)");
    context.fillStyle = gradient;
    context.fillRect(0, 0, this.width, this.height);

    const wind = direction(state.wind.angle);
    const drift = this.reducedMotion ? 0 : this.time * 7;
    context.save();
    context.globalAlpha = 0.045;
    context.fillStyle = "#98dce1";
    for (let index = 0; index < 11; index += 1) {
      const baseX = ((index * 211 + drift * wind.x) % (this.width + 300)) - 150;
      const baseY = ((index * 97 + drift * wind.y) % (this.height + 180)) - 90;
      context.beginPath();
      context.ellipse(baseX, baseY, 120 + (index % 3) * 45, 22 + (index % 2) * 10, state.wind.angle, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();

    const vignette = context.createRadialGradient(
      this.width / 2,
      this.height / 2,
      Math.min(this.width, this.height) * 0.25,
      this.width / 2,
      this.height / 2,
      Math.max(this.width, this.height) * 0.72
    );
    vignette.addColorStop(0, "rgba(0,0,0,0)");
    vignette.addColorStop(1, "rgba(0,0,0,0.52)");
    context.fillStyle = vignette;
    context.fillRect(0, 0, this.width, this.height);
  }

  private beginWorld(): void {
    this.context.save();
    this.context.beginPath();
    this.context.rect(
      this.layout.playRect.x,
      this.layout.playRect.y,
      this.layout.playRect.width,
      this.layout.playRect.height
    );
    this.context.clip();
    this.context.translate(this.origin.x, this.origin.y);
    this.context.scale(this.scale, this.scale);
  }

  private endWorld(): void {
    this.context.restore();
  }

  private drawStreamlines(state: GameState): void {
    const context = this.context;
    context.save();
    context.translate(CENTER.x, CENTER.y);
    context.rotate(state.wind.angle);
    context.lineCap = "round";
    const offset = this.reducedMotion ? 0 : (this.time * 105) % 150;

    const brightAlpha = state.tutorial.firstHit ? 0.13 : 0.22;
    const faintAlpha = state.tutorial.firstHit ? 0.07 : 0.12;
    for (let row = -3; row <= 3; row += 1) {
      const y = row * 92;
      context.strokeStyle = row % 2 === 0
        ? `rgba(145, 237, 238, ${brightAlpha})`
        : `rgba(145, 237, 238, ${faintAlpha})`;
      context.lineWidth = (row % 2 === 0 ? 2.4 : 1.4) / this.scale;
      context.setLineDash([54 / this.scale, 82 / this.scale]);
      context.lineDashOffset = -offset / this.scale;
      context.beginPath();
      context.moveTo(-650, y);
      context.bezierCurveTo(-180, y - 22 * Math.sin(row), 180, y + 22 * Math.sin(row), 650, y);
      context.stroke();
    }
    context.restore();
    context.setLineDash([]);
  }

  private drawArena(): void {
    const context = this.context;
    context.save();
    context.translate(CENTER.x, CENTER.y);
    const glow = context.createRadialGradient(0, 0, 110, 0, 0, 520);
    glow.addColorStop(0, "rgba(238, 182, 83, 0.10)");
    glow.addColorStop(0.65, "rgba(43, 119, 126, 0.05)");
    glow.addColorStop(1, "rgba(0, 0, 0, 0)");
    context.fillStyle = glow;
    context.beginPath();
    context.arc(0, 0, 520, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = "rgba(217, 173, 83, 0.2)";
    context.lineWidth = 1.5 / this.scale;
    context.setLineDash([8 / this.scale, 12 / this.scale]);
    context.beginPath();
    context.arc(0, 0, 480, 0, Math.PI * 2);
    context.stroke();
    context.restore();
    context.setLineDash([]);
  }

  private drawForecast(state: GameState): void {
    const tower = [...state.towers]
      .sort((left, right) => left.cooldown - right.cooldown)[0];
    if (!tower) {
      return;
    }
    const points = getProjectileForecast(state, tower);
    if (points.length < 2) {
      return;
    }
    const context = this.context;
    context.save();
    context.strokeStyle = state.tutorial.firstHit ? "rgba(248, 207, 104, 0.34)" : "rgba(255, 226, 132, 0.82)";
    context.lineWidth = (state.tutorial.firstHit ? 2 : 3.5) / this.scale;
    context.setLineDash([12 / this.scale, 13 / this.scale]);
    context.beginPath();
    context.moveTo(points[0]!.x, points[0]!.y);
    for (const point of points.slice(1)) {
      context.lineTo(point.x, point.y);
    }
    context.stroke();
    context.setLineDash([]);
    context.restore();
  }

  private drawHearth(state: GameState): void {
    const context = this.context;
    context.save();
    context.translate(CENTER.x, CENTER.y);
    const pulse = 1 + Math.sin(this.time * 2.4) * 0.025;
    context.scale(pulse, pulse);
    if (this.hearthstead.complete && this.hearthstead.naturalWidth > 0) {
      context.shadowColor = state.hearth.flash > 0 ? "#ff674d" : "rgba(239, 168, 70, 0.55)";
      context.shadowBlur = (state.hearth.flash > 0 ? 38 : 20) / this.scale;
      context.drawImage(this.hearthstead, -122, -122, 244, 244);
      context.shadowBlur = 0;
      context.fillStyle = state.hearth.flash > 0 ? "rgba(255, 103, 77, 0.34)" : "rgba(255, 226, 142, 0.22)";
      context.beginPath();
      context.arc(0, 0, 48, 0, Math.PI * 2);
      context.fill();
      context.restore();
      return;
    }
    context.save();
    context.rotate(state.wind.angle + Math.PI / 2);
    for (let index = 0; index < 6; index += 1) {
      context.save();
      context.rotate(index * Math.PI / 3);
      context.fillStyle = index % 2 === 0 ? "#ead8ad" : "#c86e55";
      context.strokeStyle = "#2c2430";
      context.lineWidth = 5 / this.scale;
      context.beginPath();
      context.moveTo(23, -13);
      context.quadraticCurveTo(88, -54, 112, 0);
      context.quadraticCurveTo(83, 38, 24, 13);
      context.closePath();
      context.fill();
      context.stroke();
      context.strokeStyle = "rgba(255, 245, 207, 0.55)";
      context.lineWidth = 2 / this.scale;
      context.beginPath();
      context.moveTo(30, 0);
      context.lineTo(98, 0);
      context.stroke();
      context.restore();
    }
    context.restore();
    context.shadowColor = state.hearth.flash > 0 ? "#ff674d" : "#efa846";
    context.shadowBlur = 35 / this.scale;
    context.fillStyle = "#102a2e";
    context.strokeStyle = "#e1a954";
    context.lineWidth = 4 / this.scale;
    context.beginPath();
    for (let index = 0; index < 12; index += 1) {
      const angle = index / 12 * Math.PI * 2;
      const radius = index % 2 === 0 ? 86 : 72;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    }
    context.closePath();
    context.fill();
    context.stroke();
    context.shadowBlur = 0;

    const core = context.createRadialGradient(-14, -18, 4, 0, 0, 50);
    core.addColorStop(0, "#fff1a5");
    core.addColorStop(0.35, "#f2ae45");
    core.addColorStop(0.75, "#b94a29");
    core.addColorStop(1, "#301b25");
    context.fillStyle = core;
    context.beginPath();
    context.arc(0, 0, 47, 0, Math.PI * 2);
    context.fill();

    context.strokeStyle = "rgba(255, 235, 169, 0.76)";
    context.lineWidth = 3 / this.scale;
    for (let index = 0; index < 3; index += 1) {
      context.save();
      context.rotate(this.time * 0.12 + index * Math.PI * (2 / 3));
      context.beginPath();
      context.arc(0, 0, 55 + index * 7, 0.2, 1.75);
      context.stroke();
      context.restore();
    }

    context.fillStyle = "#f2d79d";
    context.strokeStyle = "#352634";
    context.lineWidth = 3 / this.scale;
    for (const building of [
      { x: -31, y: -9, w: 24, h: 31 },
      { x: 8, y: -28, w: 27, h: 38 },
      { x: 23, y: 15, w: 22, h: 27 }
    ]) {
      context.fillRect(building.x - building.w / 2, building.y - building.h / 2, building.w, building.h);
      context.strokeRect(building.x - building.w / 2, building.y - building.h / 2, building.w, building.h);
    }
    context.restore();
  }

  private drawTowers(state: GameState): void {
    for (const tower of state.towers) {
      this.drawTower(tower, state);
    }
  }

  private drawTower(tower: Tower, state: GameState): void {
    const context = this.context;
    const position = {
      x: CENTER.x + Math.cos(tower.angle) * tower.radius,
      y: CENTER.y + Math.sin(tower.angle) * tower.radius
    };
    context.save();
    context.translate(position.x, position.y);
    context.rotate(getTowerLaunchAngle(state, tower) + Math.PI / 2);
    if (this.kiteBallista.complete && this.kiteBallista.naturalWidth > 0) {
      context.shadowColor = "rgba(239, 182, 83, 0.56)";
      context.shadowBlur = 14 / this.scale;
      context.drawImage(this.kiteBallista, -52, -54, 104, 104);
      context.shadowBlur = 0;
      context.restore();
      return;
    }
    context.shadowColor = "rgba(239, 182, 83, 0.5)";
    context.shadowBlur = 14 / this.scale;
    context.fillStyle = "#d99b45";
    context.strokeStyle = "#ffe4a0";
    context.lineWidth = 2.4 / this.scale;
    context.beginPath();
    context.moveTo(0, -42);
    context.lineTo(31, 25);
    context.lineTo(0, 17);
    context.lineTo(-31, 25);
    context.closePath();
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    context.fillStyle = "#2a2730";
    context.fillRect(-5, -31, 10, 58);
    context.fillStyle = "#f4d277";
    context.fillRect(-2, -36, 4, 51);
    context.strokeStyle = "rgba(159, 239, 235, 0.75)";
    context.lineWidth = 2 / this.scale;
    const windDifference = shortestAngle(state.wind.targetAngle, state.wind.angle);
    context.beginPath();
    context.moveTo(0, 8);
    context.quadraticCurveTo(Math.sin(windDifference) * 22, 30, Math.sin(windDifference) * 30, 45);
    context.stroke();
    context.restore();
  }

  private drawProjectiles(projectiles: Projectile[]): void {
    const context = this.context;
    context.lineCap = "round";
    context.lineJoin = "round";
    for (const projectile of projectiles) {
      const curved = projectile.turnAmount >= 0.42;
      const points = projectile.trail.length > 1 ? projectile.trail : [projectile.previous, projectile.position];
      context.strokeStyle = curved ? "rgba(88, 235, 241, 0.30)" : "rgba(245, 176, 72, 0.24)";
      context.lineWidth = 14 / this.scale;
      context.beginPath();
      context.moveTo(points[0]!.x, points[0]!.y);
      for (const point of points.slice(1)) {
        context.lineTo(point.x, point.y);
      }
      context.stroke();
      context.strokeStyle = curved ? "#69eaf0" : "#eeb64f";
      context.lineWidth = 6 / this.scale;
      context.stroke();
      context.strokeStyle = curved ? "#e1ffff" : "#fff5c7";
      context.lineWidth = 2 / this.scale;
      context.stroke();
      const flightAngle = Math.atan2(projectile.velocity.y, projectile.velocity.x);
      context.save();
      context.translate(projectile.position.x, projectile.position.y);
      context.rotate(flightAngle);
      context.fillStyle = curved ? "#e5ffff" : "#fff8dc";
      context.shadowColor = curved ? "#60ecf2" : "#f5bd56";
      context.shadowBlur = 13 / this.scale;
      context.beginPath();
      context.moveTo(projectile.radius * 1.8, 0);
      context.lineTo(-projectile.radius, -projectile.radius * 0.75);
      context.lineTo(-projectile.radius * 0.35, 0);
      context.lineTo(-projectile.radius, projectile.radius * 0.75);
      context.closePath();
      context.fill();
      context.shadowBlur = 0;
      context.restore();
    }
  }

  private drawEnemies(state: GameState): void {
    const targeted = new Set(state.towers.map((tower) => tower.targetId).filter((id): id is number => id !== null));
    for (const enemy of state.enemies) {
      this.drawEnemy(enemy, targeted.has(enemy.id));
    }
  }

  private drawEnemy(enemy: Enemy, targeted: boolean): void {
    const profile = ENEMY_PROFILES[enemy.kind];
    const context = this.context;
    const angle = Math.atan2(enemy.velocity.y, enemy.velocity.x);
    const damageFlash = enemy.flash > 0;
    context.save();
    context.translate(enemy.position.x, enemy.position.y);
    context.rotate(angle + Math.PI / 2);
    const visualScale = enemy.kind === "tempest" ? 1.15 : 1.3;
    context.scale(visualScale, visualScale);
    context.shadowColor = profile.color;
    context.shadowBlur = damageFlash ? 28 / this.scale : 12 / this.scale;
    context.fillStyle = damageFlash ? "#fff7d7" : profile.color;
    context.strokeStyle = damageFlash ? "#ffffff" : "#2b1720";
    context.lineWidth = 3 / this.scale;

    if (this.drawEnemySprite(enemy, damageFlash)) {
      // Authored cutouts carry the material detail; code-driven brackets and bars carry state.
    } else if (enemy.kind === "stoneback") {
      this.polygon(7, enemy.radius, 0.12);
      context.fill();
      context.stroke();
      context.fillStyle = "rgba(35, 31, 37, 0.58)";
      this.polygon(6, enemy.radius * 0.57, -0.2);
      context.fill();
    } else if (enemy.kind === "tacker") {
      context.beginPath();
      context.moveTo(0, -enemy.radius * 1.4);
      context.lineTo(enemy.radius * 0.55, enemy.radius);
      context.lineTo(0, enemy.radius * 0.58);
      context.lineTo(-enemy.radius * 0.55, enemy.radius);
      context.closePath();
      context.fill();
      context.stroke();
      context.strokeStyle = "#ffe2a1";
      context.beginPath();
      context.moveTo(0, -enemy.radius);
      context.lineTo(0, enemy.radius * 0.8);
      context.stroke();
    } else if (enemy.kind === "tempest") {
      for (let index = 0; index < 5; index += 1) {
        context.save();
        context.rotate(index * Math.PI * 0.4 + this.time * 0.12);
        context.beginPath();
        context.moveTo(0, -enemy.radius);
        context.quadraticCurveTo(enemy.radius * 0.9, -enemy.radius * 0.25, enemy.radius * 0.22, enemy.radius * 0.72);
        context.lineTo(0, enemy.radius * 0.3);
        context.closePath();
        context.fill();
        context.stroke();
        context.restore();
      }
      context.fillStyle = "#351928";
      context.beginPath();
      context.arc(0, 0, enemy.radius * 0.33, 0, Math.PI * 2);
      context.fill();
    } else {
      context.beginPath();
      context.moveTo(0, -enemy.radius * 1.18);
      context.quadraticCurveTo(enemy.radius * 1.05, -enemy.radius * 0.15, enemy.radius * 0.72, enemy.radius);
      context.lineTo(0, enemy.radius * 0.52);
      context.lineTo(-enemy.radius * 0.72, enemy.radius);
      context.quadraticCurveTo(-enemy.radius * 1.05, -enemy.radius * 0.15, 0, -enemy.radius * 1.18);
      context.fill();
      context.stroke();
      if (enemy.kind === "cinder") {
        context.fillStyle = "#fff0a4";
        context.beginPath();
        context.arc(0, 0, enemy.radius * 0.26, 0, Math.PI * 2);
        context.fill();
      }
    }
    context.shadowBlur = 0;
    context.restore();

    const screenX = this.origin.x + enemy.position.x * this.scale;
    const screenY = this.origin.y + enemy.position.y * this.scale;
    const rect = this.layout.playRect;
    const fullyVisible =
      screenX >= rect.x + 58 && screenX <= rect.x + rect.width - 58 &&
      screenY >= rect.y + 58 && screenY <= rect.y + rect.height - 58;

    if (targeted && fullyVisible) {
      const bracket = enemy.radius * 1.48;
      context.save();
      context.translate(enemy.position.x, enemy.position.y);
      context.strokeStyle = "#f6d575";
      context.lineWidth = 3 / this.scale;
      context.setLineDash([10 / this.scale, 8 / this.scale]);
      context.beginPath();
      context.arc(0, 0, bracket, 0, Math.PI * 2);
      context.stroke();
      context.setLineDash([]);
      context.restore();
    }

    if (fullyVisible && (enemy.health < enemy.maxHealth || enemy.kind === "tempest" || targeted)) {
      const width = enemy.kind === "tempest" ? 130 : enemy.radius * 2;
      const y = enemy.position.y + enemy.radius + 16;
      context.fillStyle = "rgba(1, 6, 12, 0.75)";
      context.fillRect(enemy.position.x - width / 2, y, width, 12 / this.scale);
      context.fillStyle = profile.color;
      context.fillRect(enemy.position.x - width / 2, y, width * Math.max(0, enemy.health / enemy.maxHealth), 12 / this.scale);
    }

    const impactTime = getEstimatedImpactTime(enemy);
    if (fullyVisible && (impactTime < 5.5 || enemy.kind === "tempest")) {
      context.save();
      context.font = `900 ${14 / this.scale}px "Nunito Sans", sans-serif`;
      context.textAlign = "center";
      context.textBaseline = "bottom";
      context.lineWidth = 4 / this.scale;
      context.strokeStyle = "rgba(2, 8, 13, 0.9)";
      context.fillStyle = "#fff0cf";
      const label = impactTime < 5.5 && enemy.kind !== "tempest"
        ? `IMPACT ${Math.max(1, Math.ceil(impactTime))}s`
        : profile.name.toUpperCase();
      context.strokeText(label, enemy.position.x, enemy.position.y - enemy.radius - 12);
      context.fillText(label, enemy.position.x, enemy.position.y - enemy.radius - 12);
      context.restore();
    }
  }

  private drawEnemySprite(enemy: Enemy, damageFlash: boolean): boolean {
    const context = this.context;
    if (enemy.kind === "tempest") {
      if (!this.redTempest.complete || this.redTempest.naturalWidth === 0) {
        return false;
      }
      const size = enemy.radius * 3.15;
      context.filter = damageFlash ? "brightness(1.75) saturate(0.55)" : "none";
      context.drawImage(this.redTempest, -size / 2, -size / 2, size, size);
      context.filter = "none";
      return true;
    }
    if (!this.raiderAtlas.complete || this.raiderAtlas.naturalWidth === 0) {
      return false;
    }
    const halfWidth = this.raiderAtlas.naturalWidth / 2;
    const halfHeight = this.raiderAtlas.naturalHeight / 2;
    const quadrant: Record<Exclude<Enemy["kind"], "tempest">, { column: number; row: number }> = {
      skiff: { column: 0, row: 0 },
      stoneback: { column: 1, row: 0 },
      tacker: { column: 0, row: 1 },
      cinder: { column: 1, row: 1 }
    };
    const source = quadrant[enemy.kind];
    const size = enemy.radius * (enemy.kind === "stoneback" ? 3.1 : 3.45);
    context.filter = damageFlash ? "brightness(1.75) saturate(0.55)" : "none";
    context.drawImage(
      this.raiderAtlas,
      source.column * halfWidth,
      source.row * halfHeight,
      halfWidth,
      halfHeight,
      -size / 2,
      -size / 2,
      size,
      size
    );
    context.filter = "none";
    return true;
  }

  private polygon(sides: number, radius: number, rotation: number): void {
    const context = this.context;
    context.beginPath();
    for (let index = 0; index < sides; index += 1) {
      const angle = rotation + index / sides * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    }
    context.closePath();
  }

  private updateParticles(dt: number, state: GameState): void {
    const wind = direction(state.wind.angle);
    for (const particle of this.particles) {
      particle.vx += wind.x * 24 * dt;
      particle.vy += wind.y * 24 * dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.life -= dt;
    }
    this.particles = this.particles.filter((particle) => particle.life > 0);
    if (this.particles.length > 240) {
      this.particles.splice(0, this.particles.length - 240);
    }
  }

  private drawParticles(): void {
    const context = this.context;
    for (const particle of this.particles) {
      const ratio = particle.life / particle.maxLife;
      context.globalAlpha = ratio;
      context.fillStyle = particle.color;
      context.beginPath();
      context.arc(particle.x, particle.y, particle.size * (0.4 + ratio * 0.6), 0, Math.PI * 2);
      context.fill();
    }
    context.globalAlpha = 1;
  }

  private burst(x: number, y: number, color: string, count: number, speed: number): void {
    const reducedCount = this.reducedMotion ? Math.ceil(count * 0.35) : count;
    for (let index = 0; index < reducedCount; index += 1) {
      const angle = index / reducedCount * Math.PI * 2 + Math.sin(index * 17.1) * 0.25;
      const velocity = speed * (0.45 + (index % 5) / 7);
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        life: 0.35 + (index % 4) * 0.08,
        maxLife: 0.6,
        color,
        size: 3 + index % 4
      });
    }
  }

  private drawOffscreenThreats(state: GameState): void {
    const context = this.context;
    const rect = this.layout.playRect;
    const center = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
    for (const enemy of state.enemies) {
      const screen = {
        x: this.origin.x + enemy.position.x * this.scale,
        y: this.origin.y + enemy.position.y * this.scale
      };
      const margin = 28;
      if (
        screen.x >= rect.x + margin && screen.x <= rect.x + rect.width - margin &&
        screen.y >= rect.y + margin && screen.y <= rect.y + rect.height - margin
      ) {
        continue;
      }
      const x = Math.max(rect.x + margin, Math.min(rect.x + rect.width - margin, screen.x));
      const y = Math.max(rect.y + margin, Math.min(rect.y + rect.height - margin, screen.y));
      const angle = Math.atan2(screen.y - center.y, screen.x - center.x);
      const profile = ENEMY_PROFILES[enemy.kind];
      context.save();
      context.translate(x, y);
      context.rotate(angle);
      context.fillStyle = "rgba(6, 14, 22, 0.9)";
      context.strokeStyle = profile.color;
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(18, 0);
      context.lineTo(-10, -13);
      context.lineTo(-5, 0);
      context.lineTo(-10, 13);
      context.closePath();
      context.fill();
      context.stroke();
      context.restore();
      const impactTime = getEstimatedImpactTime(enemy);
      if (impactTime <= 6) {
        context.save();
        context.fillStyle = "#fff0cf";
        context.font = '900 11px "Nunito Sans", sans-serif';
        context.textAlign = "center";
        context.textBaseline = "top";
        context.fillText(`${Math.max(1, Math.ceil(impactTime))}s`, x, y + 17);
        context.restore();
      }
    }

    for (let index = state.nextSpawn; index < Math.min(state.schedule.length, state.nextSpawn + 3); index += 1) {
      const spawn = state.schedule[index];
      if (!spawn) {
        continue;
      }
      const until = spawn.at - state.waveTime;
      if (until < 0 || until > 1.8) {
        continue;
      }
      const directionToSpawn = direction(spawn.angle);
      const x = Math.max(rect.x + 24, Math.min(rect.x + rect.width - 24, center.x + directionToSpawn.x * rect.width));
      const y = Math.max(rect.y + 24, Math.min(rect.y + rect.height - 24, center.y + directionToSpawn.y * rect.height));
      context.save();
      context.translate(x, y);
      context.rotate(spawn.angle + Math.PI);
      context.globalAlpha = 0.55 + (1.8 - until) / 1.8 * 0.45;
      context.strokeStyle = "#f08a68";
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(-13, -10);
      context.lineTo(0, 0);
      context.lineTo(-13, 10);
      context.stroke();
      context.restore();
    }
  }

  private drawWindInstrument(state: GameState): void {
    const context = this.context;
    const radius = state.tutorial.firstHit
      ? (this.layout.portrait ? 62 : 50)
      : (this.layout.portrait ? 88 : 72);
    const x = this.origin.x + CENTER.x * this.scale;
    const y = this.origin.y + CENTER.y * this.scale;
    context.save();
    context.translate(x, y);
    context.globalAlpha = state.tutorial.firstHit ? 0.58 : 1;
    context.strokeStyle = state.tutorial.windMoved ? "rgba(143, 232, 237, 0.48)" : "rgba(143, 232, 237, 0.92)";
    context.lineWidth = state.tutorial.windMoved ? 2 : 4;
    context.setLineDash(state.tutorial.windMoved ? [7, 10] : []);
    context.beginPath();
    context.arc(0, 0, radius, 0, Math.PI * 2);
    context.stroke();
    context.setLineDash([5, 7]);
    context.rotate(state.wind.targetAngle);
    context.strokeStyle = "rgba(245, 233, 201, 0.52)";
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(-radius * 0.22, 0);
    context.lineTo(radius * 1.12, 0);
    context.stroke();
    context.setLineDash([]);
    context.rotate(state.wind.angle - state.wind.targetAngle);
    context.fillStyle = "#a5f6ed";
    context.shadowColor = "#72d5df";
    context.shadowBlur = 18;
    context.beginPath();
    context.moveTo(radius * (state.tutorial.firstHit ? 0.92 : 1.18), 0);
    context.lineTo(-radius * 0.26, -radius * 0.16);
    context.lineTo(-radius * 0.05, 0);
    context.lineTo(-radius * 0.26, radius * 0.16);
    context.closePath();
    context.fill();
    context.restore();
  }

  private drawGuidance(state: GameState): void {
    if (state.phase !== "running") {
      return;
    }
    let message = "";
    if (state.statistics.shots === 0) {
      message = "TAP A RAIDER · FIRE AN EXACT VOLLEY";
    } else if (!state.tutorial.windMoved) {
      message = "SWIPE WHILE BOLTS FLY · STEER THE WIND";
    } else if (!state.tutorial.firstHit) {
      message = "CYAN CURVES HIT HARDER · REDIRECT A BOLT";
    } else {
      return;
    }
    const context = this.context;
    context.save();
    context.fillStyle = "rgba(3, 12, 20, 0.72)";
    context.strokeStyle = "rgba(143, 232, 237, 0.36)";
    context.lineWidth = 1;
    const width = Math.min(520, this.width - 32);
    const x = this.width / 2 - width / 2;
    const y = this.layout.portrait
      ? this.layout.playRect.y + this.layout.playRect.height + 8
      : this.height - 60;
    this.roundedRect(x, y, width, 44, 16);
    context.fill();
    context.stroke();
    context.fillStyle = "#f5e9c9";
    context.font = `700 ${Math.min(14, Math.max(11, this.width / 34))}px "Nunito Sans", sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(message, this.width / 2, y + 22);
    context.restore();
  }

  private roundedRect(x: number, y: number, width: number, height: number, radius: number): void {
    this.context.beginPath();
    this.context.roundRect(x, y, width, height, radius);
  }
}
